import { useEffect, useState } from 'react';

import { EntityRefUtil, HIGHER_UNIT_READING_MULTIPLIER, RecordState } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetPortal, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useToast } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { getAssetByIdQueryOptions } from '@/queries/assets';
import { ApiService } from '@/services/api-v2';
import { getWorkspaceRoute } from '@/utils/routes';

import CloseIcon from '../icons/close';
import PlusIcon from '../icons/plus';
import WarningIcon from '../icons/warning';
import WarningTriangleIcon from '../icons/warning-triangle';
import { TablePageHeaderActionButton } from '../table-page/table-page-header';
import { AsyncSelect } from '../ui/async-select';
import { inputVariants } from '../ui/input';
import { Label } from '../ui/label';

const formSchema = z.object({
  unit: z.object({
    label: z.string(),
    value: z.string(),
    abbreviation: z.string(),
  }),
  reading: z.number({ message: 'Reading is required' }).min(1, 'Reading is required'),
});

export default function CreateReadingLogDrawer({
  open: openProp,
  onOpenChange,
  assetId,
  noTrigger = false,
  callback,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  assetId?: string;
  noTrigger?: boolean;
  callback?: () => void;
}) {
  const toast = useToast();
  const [open, setOpen] = useState(openProp ?? false);

  useEffect(() => {
    setOpen(openProp ?? false);
  }, [openProp]);

  const handleOpenChange = (open: boolean) => {
    onOpenChange?.(open);
    setOpen(open);
    if (!open) {
      form.reset();
    }
  };

  const { data: assetData } = useQuery({ ...getAssetByIdQueryOptions({ assetId }), enabled: !!assetId && open });

  const { mutate: createUnitReading } = useMutation({
    mutationFn: async (data: z.infer<typeof formSchema>) => {
      if (!assetId) throw new Error('Asset ID is required');

      return await ApiService.CreateUnitReading({
        unitId: data.unit.value,
        assetId: assetId,
        reading: data.reading,
      });
    },
    onSuccess: () => {
      toast.success('Reading log created successfully');
      handleOpenChange(false);
      callback?.();
      form.reset();
    },
    onError: (error) => {
      toast.error(`Error creating reading log: ${error.message}`);
    },
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });
  const { watch } = form;
  const unit = watch('unit');

  function handleSubmit(data: z.infer<typeof formSchema>) {
    createUnitReading(data);
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      {!noTrigger && (
        <SheetTrigger asChild>
          <TablePageHeaderActionButton icon={<PlusIcon />} label="Create Reading Log" variant="success" kind="primary" onClick={() => setOpen(true)} />
        </SheetTrigger>
      )}
      <SheetPortal>
        <SheetContent hideCloseIcon>
          <form className="flex h-full flex-1 flex-col" onSubmit={form.handleSubmit(handleSubmit)}>
            <SheetHeader className="border-grey-3 flex-row items-center justify-between space-y-0 border-b bg-white">
              <SheetTitle>Create Reading Log</SheetTitle>
              <VisuallyHidden>
                <SheetDescription>Create Reading Log</SheetDescription>
              </VisuallyHidden>

              <div className="flex items-center gap-2">
                <Button
                  variant="success"
                  size="default"
                  type="submit"
                  disabled={form.formState.isSubmitting || !assetData}
                  // loading={isCreatingReadingLog}
                >
                  Save
                </Button>
                <button
                  type="button"
                  className="data-[state=open]:bg-default-blue rounded-sm opacity-70 transition-opacity outline-none hover:opacity-100 disabled:pointer-events-none"
                  disabled={form.formState.isSubmitting}
                  onClick={() => {
                    handleOpenChange(false);
                  }}>
                  <CloseIcon className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </button>
              </div>
            </SheetHeader>

            <div className="flex flex-1 flex-col gap-2 overflow-y-auto px-6 pt-4 pb-6">
              <div className="flex flex-col gap-1">
                <Label>Asset</Label>
                <div className="bg-grey-1 ring-grey-3 flex min-h-[41px] flex-wrap items-center gap-1 rounded p-2 text-sm ring-2 ring-inset">
                  {assetData ? (
                    <Link to={getWorkspaceRoute(`/assets/${assetData.id}`)} className="text-default-blue underline">
                      {EntityRefUtil.createAssetLabel(assetData)}
                    </Link>
                  ) : (
                    <div className="text-sm">-</div>
                  )}
                </div>
              </div>

              <Controller
                control={form.control}
                name="unit"
                render={({ field: { value, onChange } }) => (
                  <Label className="flex flex-col gap-1">
                    <span>Unit:</span>
                    <AsyncSelect
                      aria-label="Unit"
                      value={value}
                      onChange={onChange}
                      defaultOptions
                      loadOptions={async (input: string) => {
                        const { data } = await (await ApiService.GetUnits({ limit: 100, filters: { search: input, state: RecordState.Active } })).json();
                        return data?.map((unit) => ({ value: unit.id, label: unit.name, abbreviation: unit.abbreviation }));
                      }}
                    />
                  </Label>
                )}
              />

              <Controller
                control={form.control}
                name="reading"
                render={({ field: { value, onChange, onBlur }, fieldState: { error, isTouched } }) => {
                  const lastReading = assetData?.unitReadings?.find((reading) => reading.unitId === unit?.value)?.reading ?? 0;
                  const highReadingDifference = value && value > lastReading * HIGHER_UNIT_READING_MULTIPLIER;
                  const lowerReadingDifference = value && value < lastReading;

                  return (
                    <Label className="flex flex-col gap-1">
                      <span>Reading:</span>
                      <NumericFormat
                        className={cn(inputVariants({ error: !!error }), 'disabled:bg-grey-1')}
                        value={value ?? ''}
                        onValueChange={(values) => {
                          onChange(values.floatValue ?? '');
                        }}
                        onBlur={onBlur}
                        isAllowed={({ floatValue }) => floatValue === undefined || floatValue <= 10000000}
                        placeholder="Enter number..."
                        allowNegative={false}
                        thousandSeparator=","
                        decimalScale={2}
                        suffix={` ${unit?.abbreviation || ''}`}
                        inputMode="decimal"
                      />

                      <span className="flex items-center gap-2 text-sm">
                        {!error && ((!highReadingDifference && !lowerReadingDifference) || !isTouched) && unit && (
                          <span className="flex items-center">
                            Last reading: {lastReading.toLocaleString('en-US', { maximumFractionDigits: 0 })} {unit?.abbreviation}
                          </span>
                        )}
                        {!error && highReadingDifference && isTouched && (
                          <span className="flex items-center gap-2">
                            <WarningIcon size={20} className="text-default-orange flex-shrink-0" />
                            <span>
                              Much higher than the last reading: {lastReading.toLocaleString('en-US', { maximumFractionDigits: 2 })} {unit?.abbreviation}
                            </span>
                          </span>
                        )}
                        {!error && lowerReadingDifference && isTouched && (
                          <span className="flex items-center gap-2">
                            <WarningIcon size={20} className="text-default-orange flex-shrink-0" />
                            <span>
                              Lower than last reading {lastReading.toLocaleString('en-US', { maximumFractionDigits: 2 })} {unit?.abbreviation}
                            </span>
                          </span>
                        )}

                        {error && (
                          <div className="text-default-red flex items-center gap-2 text-sm">
                            <WarningTriangleIcon size={20} className="flex-shrink-0" />
                            <span>{error.message}</span>
                          </div>
                        )}
                      </span>
                    </Label>
                  );
                }}
              />
            </div>
          </form>
        </SheetContent>
      </SheetPortal>
    </Sheet>
  );
}
